import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface VideoUploadProps {
  onUploadComplete?: (transmission: any) => void;
  editTransmission?: any;
  onEditComplete?: () => void;
  tableName?: string; // New prop to specify which table/endpoint to use
}

export default function VideoUpload({ onUploadComplete, editTransmission, onEditComplete, tableName = 'transmissions' }: VideoUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    title: editTransmission?.title || '',
    description: editTransmission?.description || '',
    category: editTransmission?.category || 'Visuals'
  });

  // Determine the endpoint based on tableName
  const getEndpoint = () => {
    switch (tableName) {
      case 'collective_videos':
        return 'upload-collective-video';
      default:
        return 'upload-transmission';
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fileInput = form.querySelector('input[type="file"]') as HTMLInputElement;
    const file = fileInput.files?.[0];

    if (!editTransmission && !file) {
      alert('Please select a video file');
      return;
    }

    // Check file size (warn if over 50MB)
    if (file && file.size > 50 * 1024 * 1024) {
      const confirmUpload = confirm('Your video is quite large. This may take a while to upload. Continue?');
      if (!confirmUpload) return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formDataToSend = new FormData();
      if (file) {
        formDataToSend.append('video', file);
      }
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      
      // Only send category for transmissions table
      if (tableName === 'transmissions') {
        formDataToSend.append('category', formData.category);
      }
      
      if (editTransmission) {
        formDataToSend.append('transmissionId', editTransmission.id);
        formDataToSend.append('isUpdate', 'true');
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      console.log('Starting upload to:', getEndpoint());
      console.log('File size:', file?.size, 'bytes');
      
      // Use XMLHttpRequest for better progress tracking
      const uploadPromise = new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Track upload progress
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 90); // 0-90% for upload
            setUploadProgress(percentComplete);
            console.log(`Upload progress: ${percentComplete}%`);
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadProgress(100);
            try {
              const result = JSON.parse(xhr.responseText);
              resolve(result);
            } catch (e) {
              reject(new Error('Invalid response from server'));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`));
          }
        });
        
        xhr.addEventListener('error', () => {
          reject(new Error('Network error occurred during upload'));
        });
        
        xhr.addEventListener('timeout', () => {
          reject(new Error('Upload timed out. Please check your connection and try again.'));
        });
        
        xhr.open('POST', `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/${getEndpoint()}`);
        xhr.setRequestHeader('Authorization', `Bearer ${session?.access_token || import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`);
        xhr.timeout = 600000; // 10 minute timeout
        
        xhr.send(formDataToSend);
      });

      const result: any = await uploadPromise;
      
      console.log('Upload successful:', result);
      
      if (editTransmission && onEditComplete) {
        onEditComplete();
      } else if (onUploadComplete) {
        onUploadComplete(result.data);
      }

      // Reset form if not editing
      if (!editTransmission) {
        setFormData({ title: '', description: '', category: 'Visuals' });
        form.reset();
      }
      
    } catch (error: any) {
      console.error('Upload error:', error);
      
      // More specific error messages
      if (error.message.includes('timeout') || error.message.includes('timed out')) {
        alert('⏱️ Upload timed out.\n\nTips:\n• Try compressing your video to under 30MB\n• Use a faster internet connection\n• Try uploading during off-peak hours');
      } else if (error.message.includes('Network error')) {
        alert('🌐 Network error occurred.\n\nPlease check your internet connection and try again.');
      } else if (error.message.includes('413') || error.message.includes('too large')) {
        alert('📦 File is too large.\n\nPlease compress your video to under 50MB and try again.');
      } else {
        alert(`${editTransmission ? 'Update' : 'Upload'} failed:\n\n${error.message}`);
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="bg-black/20 backdrop-blur-sm border border-purple-500/30 rounded-lg p-6">
      <h3 className="text-xl font-bold text-white mb-4">
        {editTransmission ? 'Edit Transmission' : 'Upload New Transmission'}
      </h3>
      
      {!editTransmission && (
        <div className="mb-4 p-3 bg-purple-900/30 border border-purple-500/30 rounded-md">
          <p className="text-purple-300 text-sm">
            <strong>Video Size Tips:</strong> Use 16:9 aspect ratio (1920x1080, 1280x720, or 854x480) for best display. 
            Vertical or square videos will be cropped.
          </p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-purple-300 text-sm font-medium mb-2">
            Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-purple-400"
            placeholder="Enter transmission title"
            required
          />
        </div>

        <div>
          <label className="block text-purple-300 text-sm font-medium mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 h-20 resize-none"
            placeholder="Describe this transmission..."
            maxLength={500}
          />
        </div>

        {tableName === 'transmissions' && (
          <div>
            <label className="block text-purple-300 text-sm font-medium mb-2">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-md text-white focus:outline-none focus:border-purple-400 pr-8"
            >
              <option value="Visuals">Visuals</option>
              <option value="Hybrid">Hybrid</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-purple-300 text-sm font-medium mb-2">
            Video File {editTransmission && '(Leave empty to keep current video)'}
          </label>
          <input
            type="file"
            accept="video/*"
            className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-md text-white file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer hover:file:bg-purple-700"
            required={!editTransmission}
          />
        </div>

        {isUploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-purple-300">
              <span>{editTransmission ? 'Updating...' : 'Uploading...'}</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-black/30 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={isUploading}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 px-4 rounded-md hover:from-purple-700 hover:to-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isUploading 
            ? (editTransmission ? 'Updating...' : 'Uploading...') 
            : (editTransmission ? 'Update Transmission' : 'Upload Transmission')
          }
        </button>
      </form>
    </div>
  );
}
